import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  MapPin,
  Warehouse,
  Truck,
  GraduationCap,
  Handshake,
  Package,
} from "lucide-react";

const POLYMER_IMG =
  "https://images.unsplash.com/photo-1615565016433-69797a5e3e11?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const LOGISTICS_IMG =
  "https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const CSR_IMG =
  "https://images.pexels.com/photos/8423416/pexels-photo-8423416.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

function Eyebrow({ children, tone = "zinc" }) {
  return (
    <div
      className={`flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] ${
        tone === "light" ? "text-zinc-400" : "text-zinc-500"
      }`}
    >
      <span className="h-[6px] w-[6px] bg-orange-500" />
      {children}
    </div>
  );
}

export default function Business() {
  return (
    <div data-testid="business-page">
      {/* HERO */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 pt-20 pb-16 md:px-12 md:pt-28 md:pb-24 lg:px-16">
          <Eyebrow>Our Business</Eyebrow>
          <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tighter text-zinc-950 md:text-7xl lg:text-8xl">
            What we do.
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-600">
            A commitment to quality trade, strong distribution, and social
            impact — engineered for manufacturers and communities across India.
          </p>

          <div className="mt-12 grid grid-cols-1 gap-3 md:grid-cols-3">
            {[
              { k: "Vertical 01", v: "PET Resin Trading & Import" },
              { k: "Vertical 02", v: "Authorized Distribution" },
              { k: "Vertical 03", v: "CSR & Skilling" },
            ].map((item, i) => (
              <a
                key={item.k}
                href={`#section-${i + 1}`}
                data-testid={`business-jump-${i + 1}`}
                className="group flex items-center justify-between border border-zinc-200 bg-white p-5 transition-colors hover:border-zinc-950"
              >
                <div>
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    {item.k}
                  </div>
                  <div className="mt-1 font-heading text-base font-bold text-zinc-950">
                    {item.v}
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-zinc-950" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SECTION 1: PET RESIN ---------- */}
      <section
        id="section-1"
        data-testid="business-pet-resin"
        className="border-b border-zinc-200"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
            <div className="md:col-span-6">
              <Eyebrow>01 · PET Resin Trading & Import</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                The foundation of modern bottling.
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-zinc-700">
                PET (Polyethylene Terephthalate) is one of the most widely used
                polymers in the world — valued for its strength, clarity, and
                recyclability. At Agrim Ventures, we specialize in the trading
                and import of bottle-grade PET resin, serving manufacturers in
                the beverage, bottling, and packaging industries.
              </p>

              <div className="mt-10">
                <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  Applications We Serve
                </div>
                <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {[
                    "Beverage and water bottles",
                    "Carbonated soft drink bottles",
                    "Food and edible oil bottles",
                    "Pharmaceutical bottles",
                    "Industrial containers",
                    "Consumer goods packaging",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-700"
                    >
                      <CheckCircle2
                        className="mt-[2px] h-4 w-4 flex-shrink-0 text-orange-500"
                        strokeWidth={1.8}
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="md:col-span-6">
              <div className="relative h-[420px] w-full overflow-hidden border border-zinc-200 md:h-[520px]">
                <img
                  src={POLYMER_IMG}
                  alt="PET polymer granules"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-6 right-6 bottom-6 border border-zinc-950 bg-white p-6 md:left-8 md:right-auto md:bottom-8 md:max-w-sm">
                  <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-orange-500">
                    Quality Promise
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-700">
                    Every batch sourced from reputed manufacturers, tested for
                    consistency, and delivered in compliance with industry
                    standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- SECTION 2: DISTRIBUTION ---------- */}
      <section
        id="section-2"
        data-testid="business-distribution"
        className="border-b border-zinc-200 bg-zinc-50"
      >
        <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32 lg:px-16">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <Eyebrow>02 · Authorized Distributor</Eyebrow>
              <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-zinc-950 md:text-5xl">
                A trusted partnership with Chiripal Polyfilms Ltd.
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-zinc-700">
                We are proud to serve as an authorized distributor for{" "}
                <span className="text-zinc-950">Chiripal Polyfilms Ltd</span> —
                one of India's leading manufacturers of polyester films and
                packaging solutions. Through this partnership, we bring
                world-class polymer products to manufacturers and brands
                through a wide, dependable distribution network.
              </p>
            </div>

            <div className="md:col-span-7">
              <div className="relative h-[320px] overflow-hidden border border-zinc-200 md:h-[380px]">
                <img
                  src={LOGISTICS_IMG}
                  alt="Distribution logistics"
                  className="h-full w-full object-cover grayscale"
                />
              </div>
            </div>
          </div>

          <div className="mt-16">
            <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              What sets our distribution apart
            </div>
            <div className="mt-6 grid grid-cols-1 gap-px bg-zinc-200 md:grid-cols-4">
              {[
                { icon: Handshake, label: "Responsive customer service and long-term relationships" },
                { icon: Warehouse, label: "Efficient warehousing and logistics" },
                { icon: MapPin, label: "Wide distribution network with deep on-ground presence" },
                { icon: Truck, label: "Reliable order fulfillment with minimal lead times" },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-white p-6"
                  data-testid={`distribution-feature-${i + 1}`}
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-zinc-950 text-zinc-950">
                    <item.icon className="h-4 w-4" strokeWidth={1.6} />
                  </div>
                  <p className="text-sm leading-relaxed text-zinc-700">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- SECTION 3: CSR ---------- */}
      <section
        id="section-3"
        data-testid="business-csr"
        className="border-b border-zinc-200"
      >
        <div className="relative">
          <div className="relative h-[320px] w-full overflow-hidden md:h-[420px]">
            <img
              src={CSR_IMG}
              alt="CSR classroom skilling"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute inset-0 flex items-end">
              <div className="mx-auto w-full max-w-[1400px] px-6 pb-10 md:px-12 md:pb-14 lg:px-16">
                <div className="flex items-center gap-2 font-mono-plex text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                  <span className="h-[6px] w-[6px] bg-orange-500" />
                  03 · CSR & Skilling
                </div>
                <h2 className="mt-4 font-heading text-4xl font-black leading-[1] tracking-tighter text-white md:text-7xl">
                  Building capability.
                  <br />
                  <span className="italic font-light">Empowering</span> lives.
                </h2>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-24 lg:px-16">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="text-lg leading-relaxed text-zinc-700">
                  At Agrim Ventures, we believe that every business has a
                  responsibility beyond profits. Our CSR efforts are focused on
                  one of India's most urgent priorities — skilling the next
                  generation of workers.
                </p>
              </div>

              <div className="md:col-span-7">
                <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  Our Approach
                </div>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="border border-zinc-200 bg-white p-6">
                    <div className="font-heading text-lg font-bold text-zinc-950">
                      In-house Skilling
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                      Designed and executed by our team to directly train youth
                      in relevant vocational and industry-ready skills.
                    </p>
                  </div>
                  <div className="border border-zinc-200 bg-white p-6">
                    <div className="font-heading text-lg font-bold text-zinc-950">
                      Outsourced Partnerships
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-600">
                      Collaborations with trusted training organizations to
                      widen our reach and impact.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Focus areas */}
            <div className="mt-20">
              <div className="font-mono-plex text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                Focus Areas
              </div>
              <div className="mt-6 grid grid-cols-1 gap-px bg-zinc-200 md:grid-cols-4">
                {[
                  "Vocational training for industrial trades",
                  "Employability & soft-skill development",
                  "Placement-linked training programs",
                  "Support for underserved youth & communities",
                ].map((f, i) => (
                  <div key={i} className="bg-white p-6">
                    <div className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-zinc-400">
                      0{i + 1}
                    </div>
                    <p className="mt-4 font-heading text-base font-semibold leading-snug text-zinc-950">
                      {f}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Impact narrative */}
            <div className="mt-20 bg-zinc-950 p-8 text-white md:p-16">
              <Eyebrow tone="light">A Strong, Ongoing Initiative</Eyebrow>
              <h3 className="mt-6 max-w-4xl font-heading text-3xl font-bold leading-tight tracking-tight md:text-5xl">
                Skilling is not a side project — it's a commitment we renew
                every day.
              </h3>
              <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3">
                <div>
                  <GraduationCap className="h-7 w-7 text-orange-400" strokeWidth={1.4} />
                  <div className="mt-5 font-heading text-xl font-bold">
                    Lives touched, futures opened
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                    Every cohort we train is a step toward helping young people
                    earn with dignity and confidence.
                  </p>
                </div>
                <div>
                  <Handshake className="h-7 w-7 text-orange-400" strokeWidth={1.4} />
                  <div className="mt-5 font-heading text-xl font-bold">
                    Built with trusted partners
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                    We collaborate with credible training organizations to
                    deliver meaningful, industry-ready programs.
                  </p>
                </div>
                <div>
                  <Package className="h-7 w-7 text-orange-400" strokeWidth={1.4} />
                  <div className="mt-5 font-heading text-xl font-bold">
                    Grounded in real outcomes
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                    Our focus is on employability and placement — not awareness
                    alone — so skilling translates to livelihoods.
                  </p>
                </div>
              </div>
              <p className="mt-12 max-w-2xl text-sm text-zinc-400">
                We prefer to let the work speak for itself rather than lead
                with numbers. If you'd like to know more or partner with us,
                reach out anytime.
              </p>
            </div>

            {/* CSR CTA */}
            <div className="mt-14 flex flex-col items-start justify-between gap-6 border border-zinc-200 bg-white p-8 md:flex-row md:items-center md:p-10">
              <div>
                <div className="font-heading text-2xl font-bold text-zinc-950 md:text-3xl">
                  Partner With Us on CSR
                </div>
                <p className="mt-3 max-w-2xl text-zinc-600">
                  If you're a corporate, foundation, or training organization
                  looking to collaborate on skilling projects, we'd love to
                  connect.
                </p>
              </div>
              <Link
                to="/contact"
                data-testid="csr-cta-collaborate"
                className="group inline-flex items-center gap-3 bg-zinc-950 px-6 py-4 font-mono-plex text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:bg-zinc-800"
              >
                Collaborate With Us
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
